import { isGuideTranslatedLanguageCode } from "@/lib/guides/languages";

/**
 * Widget locales deliberately use the same two-letter ISO codes as Guides.
 * `auto` is a configuration mode, not a content locale.
 */
export const WIDGET_LANGUAGE_OPTIONS = [
  { code: "auto", label: "Auto", shortLabel: "Auto" },
  { code: "en", label: "English", shortLabel: "EN" },
  { code: "it", label: "Italiano", shortLabel: "IT" },
  { code: "de", label: "Deutsch", shortLabel: "DE" },
  { code: "fr", label: "Français", shortLabel: "FR" },
] as const;

export type WidgetLanguageCode =
  (typeof WIDGET_LANGUAGE_OPTIONS)[number]["code"];
export type WidgetContentLanguage = Exclude<WidgetLanguageCode, "auto">;

const CONTENT_LANGUAGES: WidgetContentLanguage[] = ["en", "it", "de", "fr"];

export function isWidgetLanguageCode(
  value: unknown,
): value is WidgetLanguageCode {
  return WIDGET_LANGUAGE_OPTIONS.some((item) => item.code === value);
}

export function isWidgetContentLanguage(
  value: unknown,
): value is WidgetContentLanguage {
  // Keep the supported set aligned with the existing Guides locale vocabulary,
  // while intentionally shipping only the four V1 widget languages.
  return (
    typeof value === "string" &&
    CONTENT_LANGUAGES.includes(value as WidgetContentLanguage) &&
    (value === "en" || isGuideTranslatedLanguageCode(value))
  );
}

export function safeWidgetLanguage(value: unknown): WidgetLanguageCode {
  return isWidgetLanguageCode(value) ? value : "auto";
}

export function detectWidgetLanguage(
  languages?: readonly string[],
): WidgetContentLanguage {
  const candidates =
    languages ||
    (typeof navigator !== "undefined"
      ? navigator.languages || [navigator.language]
      : []);
  for (const candidate of candidates) {
    const base = String(candidate || "")
      .trim()
      .toLowerCase()
      .split(/[-_]/)[0];
    if (isWidgetContentLanguage(base)) return base;
  }
  return "en";
}

export function resolveWidgetLanguage(
  configured: WidgetLanguageCode,
): WidgetContentLanguage {
  return configured === "auto" ? detectWidgetLanguage() : configured;
}

export function formatFerryWidgetDate(
  isoDate: string,
  locale: WidgetContentLanguage,
): string {
  try {
    const d = new Date(`${isoDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return isoDate;
    const targetLocale = locale === "en" ? "en-GB" : locale;
    return new Intl.DateTimeFormat(targetLocale, {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    }).format(d);
  } catch {
    return isoDate;
  }
}

export type FerryWidgetCopy = {
  locale: WidgetContentLanguage;
  language: string;
  languageSelector: string;
  lakeComo: string;
  plannerTitle: string;
  publishedTimes: string;
  loadingStops: string;
  startingPoint: string;
  chooseFerryStop: string;
  chooseStartingPoint: string;
  destination: string;
  checkingRoutes: string;
  chooseDestination: string;
  searchFerryStops: string;
  closeStationPicker: string;
  noMatchingStops: string;
  stationResults: (count: number) => string;
  date: string;
  leaveAfter: string;
  findFerries: string;
  findingDepartures: string;
  publishedDepartures: string;
  found: (count: number) => string;
  via: (stops: string) => string;
  departure: string;
  moreDepartures: string;
  noDirectSailing: (time: string) => string;
  tryEarlier: string;
  outsideTimetable: (from: string, to: string) => string;
  chooseDestinationToSearch: string;
  publishedTimesWithValidity: (date: string) => string;
  timetableCoverageAfter: (date: string) => string;
  timetableCoverageBefore: (date: string) => string;
  noTimetableForDate: string;
  noDirectSailingsForRoute: string;
  chooseDestinationServedDirectly: (origin: string) => string;
  noDirectDestinations: string;
  unavailableTitle: string;
  unavailableBody: string;
  unavailableTryAgain: string;
  openPlanner: string;
  fullJourney: string;
  poweredBy: string;
  theme: string;
  autoTheme: string;
  lightTheme: string;
  darkTheme: string;
  softAccentBackground: string;
  whiteBackground: string;
  transparentBackground: string;
  yourWidget: string;
  nearestStop: string;
  defaultDepartureHelp: string;
  defaultDestination: string;
  letGuestsChoose: string;
  defaultDestinationHelp: string;
  originQuestion: string;
  yes: string;
  yesHelp: string;
  no: string;
  noHelp: string;
  appearance: string;
  accentColor: string;
  hexValue: string;
  accentHelp: string;
  background: string;
  livePreview: string;
  copyWidget: string;
  copyIframe: string;
  copyAttribution: string;
  copied: string;
  preparing: string;
  whereCanAdd: string;
  howAdd: string;
  platformNote: string;
  add: string;
  fallbackPreview: string;
};

const EN: FerryWidgetCopy = {
  locale: "en",
  language: "English",
  languageSelector: "Widget language",
  lakeComo: "Lake Como",
  plannerTitle: "Lake Como ferry planner",
  publishedTimes: "Published ferry times from Teravia's reviewed timetable.",
  loadingStops: "Loading ferry stops…",
  startingPoint: "Starting point",
  chooseFerryStop: "Choose a ferry stop",
  chooseStartingPoint: "Choose starting point",
  destination: "Destination",
  checkingRoutes: "Checking routes…",
  chooseDestination: "Choose a destination",
  searchFerryStops: "Search ferry stops",
  closeStationPicker: "Close station picker",
  noMatchingStops: "No matching ferry stops",
  stationResults: (count) =>
    `${count} ferry stop${count === 1 ? "" : "s"} found`,
  date: "Date",
  leaveAfter: "Leave after",
  findFerries: "Find ferries",
  findingDepartures: "Finding departures…",
  publishedDepartures: "Published departures",
  found: (count) => `${count} found`,
  via: (stops) => `Via ${stops}`,
  departure: "departure",
  moreDepartures: "More departures are available in the full Teravia planner.",
  noDirectSailing: (time) =>
    `No direct published sailing is listed after ${time}.`,
  tryEarlier:
    "Try an earlier time or open the full planner for the wider Lake Como context.",
  outsideTimetable: (from, to) =>
    `Published timetable available ${from}–${to}.`,
  chooseDestinationToSearch: "Choose a destination to search sailings",
  publishedTimesWithValidity: (date) =>
    `Published ferry times · Timetable available through ${date}`,
  timetableCoverageAfter: (date) =>
    `Published timetable data is currently available through ${date}. Later schedules may not have been published yet. Check again closer to your travel date.`,
  timetableCoverageBefore: (date) =>
    `Published timetable data is unavailable before ${date}.`,
  noTimetableForDate: "No published ferry timetable covers this date yet.",
  noDirectSailingsForRoute:
    "No published direct sailings found for this route on this date.",
  chooseDestinationServedDirectly: (origin) =>
    `Choose a destination served directly from ${origin}`,
  noDirectDestinations:
    "No published direct destinations available from this stop.",
  unavailableTitle: "Ferry information is temporarily unavailable.",
  unavailableBody:
    "Please use the full Lake Como planner while we reconnect the published timetable.",
  unavailableTryAgain: "Please try again or open the full planner.",
  openPlanner: "Open the Lake Como ferry planner",
  fullJourney: "View journey",
  poweredBy: "Powered by Teravia",
  theme: "Theme",
  autoTheme: "Auto (follow visitor device)",
  lightTheme: "Light",
  darkTheme: "Dark",
  softAccentBackground: "Soft accent",
  whiteBackground: "White",
  transparentBackground: "Transparent",
  yourWidget: "Your widget",
  nearestStop: "Your nearest ferry stop",
  defaultDepartureHelp: "This is the widget's default departure point.",
  defaultDestination: "Default destination",
  letGuestsChoose: "Let guests choose",
  defaultDestinationHelp:
    "Leave this open unless most guests make the same crossing.",
  originQuestion: "Can guests change the starting point?",
  yes: "Yes",
  yesHelp: "Guests can start from another pier.",
  no: "No",
  noHelp: "Keep the planner focused on your nearest pier.",
  appearance: "Appearance",
  accentColor: "Accent color",
  hexValue: "Hex value",
  accentHelp:
    "Used for buttons, selections and focus states. Teravia keeps text readable automatically.",
  background: "Background",
  livePreview: "Live preview",
  copyWidget: "Copy widget",
  copyIframe: "Copy iframe",
  copyAttribution: "Copy attribution",
  copied: "Copied",
  preparing: "Preparing…",
  whereCanAdd: "Where can I add it?",
  howAdd: "How do I add it?",
  platformNote: "A quick note for your website platform",
  add: "Add",
  fallbackPreview:
    "The preview starts here; adjust the settings to see your own version.",
};

const IT: FerryWidgetCopy = {
  ...EN,
  locale: "it",
  language: "Italiano",
  languageSelector: "Lingua del widget",
  lakeComo: "Lago di Como",
  plannerTitle: "Pianificatore dei traghetti sul Lago di Como",
  publishedTimes:
    "Orari dei traghetti pubblicati nell'orario verificato di Teravia.",
  loadingStops: "Caricamento degli imbarchi…",
  startingPoint: "Partenza",
  chooseFerryStop: "Scegli un imbarco",
  chooseStartingPoint: "Scegli il punto di partenza",
  destination: "Destinazione",
  checkingRoutes: "Verifica dei collegamenti…",
  chooseDestination: "Scegli una destinazione",
  searchFerryStops: "Cerca imbarchi",
  closeStationPicker: "Chiudi la selezione dell'imbarco",
  noMatchingStops: "Nessun imbarco corrispondente",
  stationResults: (count) =>
    `${count} ${count === 1 ? "imbarco trovato" : "imbarchi trovati"}`,
  date: "Data",
  leaveAfter: "Partenza dopo le",
  findFerries: "Cerca traghetti",
  findingDepartures: "Ricerca delle partenze…",
  publishedDepartures: "Partenze pubblicate",
  found: (count) => `${count} trovate`,
  via: (stops) => `Via ${stops}`,
  departure: "partenza",
  moreDepartures: "Trovi altre partenze nel pianificatore completo di Teravia.",
  noDirectSailing: (time) =>
    `Non è pubblicata alcuna partenza diretta dopo le ${time}.`,
  tryEarlier:
    "Prova un orario precedente o apri il pianificatore completo per vedere il contesto del lago.",
  outsideTimetable: (from, to) => `Orari pubblicati dal ${from} al ${to}.`,
  chooseDestinationToSearch: "Scegli una destinazione per cercare le corse",
  publishedTimesWithValidity: (date) =>
    `Orari dei traghetti pubblicati · Disponibili fino al ${date}`,
  timetableCoverageAfter: (date) =>
    `Gli orari pubblicati sono attualmente disponibili fino al ${date}. Gli orari successivi potrebbero non essere ancora stati pubblicati. Ricontrolla più vicino alla data del viaggio.`,
  timetableCoverageBefore: (date) =>
    `Gli orari pubblicati non sono disponibili prima del ${date}.`,
  noTimetableForDate:
    "Nessun orario dei traghetti pubblicato copre ancora questa data.",
  noDirectSailingsForRoute:
    "Nessuna corsa diretta pubblicata trovata per questa tratta in questa data.",
  chooseDestinationServedDirectly: (origin) =>
    `Scegli una destinazione servita direttamente da ${origin}`,
  noDirectDestinations:
    "Nessuna destinazione diretta pubblicata disponibile da questo imbarco.",
  unavailableTitle:
    "Le informazioni sui traghetti non sono al momento disponibili.",
  unavailableBody:
    "Apri il pianificatore completo del Lago di Como mentre ricolleghiamo gli orari pubblicati.",
  unavailableTryAgain: "Riprova oppure apri il pianificatore completo.",
  openPlanner: "Apri il pianificatore dei traghetti",
  fullJourney: "Vedi viaggio",
  poweredBy: "Powered by Teravia",
  theme: "Tema",
  autoTheme: "Automatico (segue il dispositivo del visitatore)",
  lightTheme: "Chiaro",
  darkTheme: "Scuro",
  softAccentBackground: "Accento delicato",
  whiteBackground: "Bianco",
  transparentBackground: "Trasparente",
  yourWidget: "Il tuo widget",
  nearestStop: "Il tuo imbarco più vicino",
  defaultDepartureHelp:
    "Questo sarà il punto di partenza predefinito del widget.",
  defaultDestination: "Destinazione predefinita",
  letGuestsChoose: "Lascia scegliere agli ospiti",
  defaultDestinationHelp:
    "Lascia aperto se gli ospiti raggiungono destinazioni diverse.",
  originQuestion: "Gli ospiti possono cambiare il punto di partenza?",
  yes: "Sì",
  yesHelp: "Gli ospiti possono partire da un altro imbarco.",
  no: "No",
  noHelp: "Mantieni il pianificatore concentrato sull'imbarco più vicino.",
  appearance: "Aspetto",
  accentColor: "Colore in evidenza",
  hexValue: "Valore esadecimale",
  accentHelp:
    "Usato per pulsanti, selezioni e stati di focus. Teravia mantiene il testo leggibile.",
  background: "Sfondo",
  livePreview: "Anteprima live",
  copyWidget: "Copia widget",
  copyIframe: "Copia iframe",
  copyAttribution: "Copia attribuzione",
  copied: "Copiato",
  preparing: "Preparazione…",
  whereCanAdd: "Dove posso aggiungerlo?",
  howAdd: "Come posso aggiungerlo?",
  platformNote: "Una nota per la piattaforma del tuo sito",
  add: "Aggiungi",
  fallbackPreview:
    "L'anteprima parte da qui; modifica le impostazioni per vedere la tua versione.",
};

const DE: FerryWidgetCopy = {
  ...EN,
  locale: "de",
  language: "Deutsch",
  languageSelector: "Widget-Sprache",
  lakeComo: "Comer See",
  plannerTitle: "Fährplaner für den Comer See",
  publishedTimes:
    "Veröffentlichte Abfahrtszeiten aus Teravias geprüftem Fahrplan.",
  loadingStops: "Fähranleger werden geladen…",
  startingPoint: "Startpunkt",
  chooseFerryStop: "Fähranleger auswählen",
  chooseStartingPoint: "Startpunkt auswählen",
  destination: "Ziel",
  checkingRoutes: "Verbindungen werden geprüft…",
  chooseDestination: "Ziel auswählen",
  searchFerryStops: "Fähranleger suchen",
  closeStationPicker: "Anlegerauswahl schließen",
  noMatchingStops: "Keine passenden Fähranleger",
  stationResults: (count) =>
    `${count} ${count === 1 ? "Fähranleger gefunden" : "Fähranleger gefunden"}`,
  date: "Datum",
  leaveAfter: "Abfahrt nach",
  findFerries: "Fähren suchen",
  findingDepartures: "Abfahrten werden gesucht…",
  publishedDepartures: "Veröffentlichte Abfahrten",
  found: (count) => `${count} gefunden`,
  via: (stops) => `Über ${stops}`,
  departure: "Abfahrt",
  moreDepartures:
    "Weitere Abfahrten finden Sie im vollständigen Teravia-Planer.",
  noDirectSailing: (time) =>
    `Nach ${time} ist keine direkte veröffentlichte Verbindung aufgeführt.`,
  tryEarlier:
    "Versuchen Sie eine frühere Zeit oder öffnen Sie den vollständigen Planer für den weiteren Kontext.",
  outsideTimetable: (from, to) => `Veröffentlichter Fahrplan: ${from}–${to}.`,
  chooseDestinationToSearch: "Wählen Sie ein Ziel, um Verbindungen zu suchen",
  publishedTimesWithValidity: (date) =>
    `Veröffentlichte Abfahrtszeiten · Fahrplan verfügbar bis ${date}`,
  timetableCoverageAfter: (date) =>
    `Veröffentlichte Fahrplandaten sind derzeit bis zum ${date} verfügbar. Spätere Fahrpläne wurden möglicherweise noch nicht veröffentlicht. Bitte prüfen Sie dies kurz vor Ihrer Reise erneut.`,
  timetableCoverageBefore: (date) =>
    `Veröffentlichte Fahrplandaten sind vor dem ${date} nicht verfügbar.`,
  noTimetableForDate:
    "Für dieses Datum liegt noch kein veröffentlichter Fährfahrplan vor.",
  noDirectSailingsForRoute:
    "Für diese Verbindung wurden an diesem Datum keine direkten Fahrten gefunden.",
  unavailableTitle: "Fährinformationen sind vorübergehend nicht verfügbar.",
  unavailableBody:
    "Öffnen Sie den vollständigen Comer-See-Planer, während wir den veröffentlichten Fahrplan wieder verbinden.",
  unavailableTryAgain:
    "Versuchen Sie es erneut oder öffnen Sie den vollständigen Planer.",
  openPlanner: "Comer-See-Fährplaner öffnen",
  fullJourney: "Verbindung ansehen",
  poweredBy: "Powered by Teravia",
  theme: "Darstellung",
  autoTheme: "Auto (folgt dem Gerät des Besuchers)",
  lightTheme: "Hell",
  darkTheme: "Dunkel",
  softAccentBackground: "Sanfter Akzent",
  whiteBackground: "Weiß",
  transparentBackground: "Transparent",
  yourWidget: "Ihr Widget",
  nearestStop: "Ihr nächster Fähranleger",
  defaultDepartureHelp: "Dies ist der voreingestellte Abfahrtsort des Widgets.",
  defaultDestination: "Standardziel",
  letGuestsChoose: "Gäste wählen lassen",
  defaultDestinationHelp:
    "Lassen Sie dies offen, wenn Gäste verschiedene Ziele ansteuern.",
  originQuestion: "Können Gäste den Startpunkt ändern?",
  yes: "Ja",
  yesHelp: "Gäste können an einem anderen Anleger starten.",
  no: "Nein",
  noHelp: "Der Planer bleibt auf den nächstgelegenen Anleger ausgerichtet.",
  appearance: "Darstellung",
  accentColor: "Akzentfarbe",
  hexValue: "Hex-Wert",
  accentHelp:
    "Für Schaltflächen, Auswahl und Fokus. Teravia sorgt automatisch für lesbaren Text.",
  background: "Hintergrund",
  livePreview: "Live-Vorschau",
  copyWidget: "Widget kopieren",
  copyIframe: "Iframe kopieren",
  copyAttribution: "Attribution kopieren",
  copied: "Kopiert",
  whereCanAdd: "Wo kann ich es hinzufügen?",
  howAdd: "Wie füge ich es hinzu?",
  platformNote: "Kurzer Hinweis für Ihre Website-Plattform",
  add: "Hinzufügen",
  fallbackPreview:
    "Die Vorschau startet hier. Passen Sie die Einstellungen an, um Ihre Version zu sehen.",
};

const FR: FerryWidgetCopy = {
  ...EN,
  locale: "fr",
  language: "Français",
  languageSelector: "Langue du widget",
  lakeComo: "Lac de Côme",
  plannerTitle: "Planificateur de ferries du lac de Côme",
  publishedTimes: "Horaires publiés à partir de l'horaire vérifié de Teravia.",
  loadingStops: "Chargement des embarcadères…",
  startingPoint: "Départ",
  chooseFerryStop: "Choisir un embarcadère",
  chooseStartingPoint: "Choisir le point de départ",
  destination: "Destination",
  checkingRoutes: "Vérification des liaisons…",
  chooseDestination: "Choisir une destination",
  searchFerryStops: "Rechercher un embarcadère",
  closeStationPicker: "Fermer la sélection de l'embarcadère",
  noMatchingStops: "Aucun embarcadère correspondant",
  stationResults: (count) =>
    `${count} embarcadère${count > 1 ? "s" : ""} trouvé${count > 1 ? "s" : ""}`,
  date: "Date",
  leaveAfter: "Départ après",
  findFerries: "Rechercher des ferries",
  findingDepartures: "Recherche des départs…",
  publishedDepartures: "Départs publiés",
  found: (count) => `${count} trouvé${count > 1 ? "s" : ""}`,
  via: (stops) => `Via ${stops}`,
  departure: "départ",
  moreDepartures:
    "D'autres départs sont disponibles dans le planificateur complet de Teravia.",
  noDirectSailing: (time) =>
    `Aucune liaison directe publiée n'est indiquée après ${time}.`,
  tryEarlier:
    "Essayez une heure plus tôt ou ouvrez le planificateur complet pour le contexte du lac.",
  outsideTimetable: (from, to) => `Horaires publiés du ${from} au ${to}.`,
  chooseDestinationToSearch:
    "Choisissez une destination pour rechercher les départs",
  publishedTimesWithValidity: (date) =>
    `Horaires publiés · Disponibles jusqu'au ${date}`,
  timetableCoverageAfter: (date) =>
    `Les horaires publiés sont actuellement disponibles jusqu'au ${date}. Les horaires ultérieurs ne sont peut-être pas encore publiés. Vérifiez à nouveau à l'approche de votre voyage.`,
  timetableCoverageBefore: (date) =>
    `Les horaires publiés ne sont pas disponibles avant le ${date}.`,
  noTimetableForDate:
    "Aucun horaire de ferry publié ne couvre encore cette date.",
  noDirectSailingsForRoute:
    "Aucune liaison directe publiée trouvée pour cet itinéraire à cette date.",
  unavailableTitle:
    "Les informations sur les ferries sont momentanément indisponibles.",
  unavailableBody:
    "Ouvrez le planificateur complet du lac de Côme pendant la reconnexion des horaires publiés.",
  unavailableTryAgain: "Réessayez ou ouvrez le planificateur complet.",
  openPlanner: "Ouvrir le planificateur de ferries",
  fullJourney: "Voir le trajet",
  poweredBy: "Powered by Teravia",
  theme: "Thème",
  autoTheme: "Auto (selon l'appareil du visiteur)",
  lightTheme: "Clair",
  darkTheme: "Sombre",
  softAccentBackground: "Accent doux",
  whiteBackground: "Blanc",
  transparentBackground: "Transparent",
  yourWidget: "Votre widget",
  nearestStop: "Votre embarcadère le plus proche",
  defaultDepartureHelp: "C'est le point de départ par défaut du widget.",
  defaultDestination: "Destination par défaut",
  letGuestsChoose: "Laisser les visiteurs choisir",
  defaultDestinationHelp:
    "Laissez ce choix ouvert si les visiteurs vont vers plusieurs destinations.",
  originQuestion: "Les visiteurs peuvent-ils changer le point de départ ?",
  yes: "Oui",
  yesHelp: "Les visiteurs peuvent partir d'un autre embarcadère.",
  no: "Non",
  noHelp:
    "Gardez le planificateur centré sur votre embarcadère le plus proche.",
  appearance: "Apparence",
  accentColor: "Couleur d'accent",
  hexValue: "Valeur hexadécimale",
  accentHelp:
    "Utilisée pour les boutons, sélections et états de focus. Teravia préserve la lisibilité du texte.",
  background: "Arrière-plan",
  livePreview: "Aperçu en direct",
  copyWidget: "Copier le widget",
  copyIframe: "Copier l'iframe",
  copyAttribution: "Copier l'attribution",
  copied: "Copié",
  whereCanAdd: "Où puis-je l'ajouter ?",
  howAdd: "Comment l'ajouter ?",
  platformNote: "Quelques mots sur votre plateforme",
  add: "Ajouter",
  fallbackPreview:
    "L'aperçu commence ici ; modifiez les réglages pour voir votre version.",
};

const COPIES: Record<WidgetContentLanguage, FerryWidgetCopy> = {
  en: {
    ...EN,
    publishedTimesWithValidity: (date) =>
      `Published schedules available through ${date}`,
    fullJourney: "View journey",
    autoTheme: "Follow visitor device",
  },
  it: {
    ...IT,
    publishedTimesWithValidity: (date) =>
      `Orari pubblicati disponibili fino al ${date}`,
    fullJourney: "Vedi viaggio",
    autoTheme: "Segui il dispositivo del visitatore",
  },
  de: {
    ...DE,
    publishedTimesWithValidity: (date) =>
      `Veröffentlichte Fahrpläne verfügbar bis ${date}`,
    fullJourney: "Verbindung ansehen",
    autoTheme: "Gerät des Besuchers folgen",
  },
  fr: {
    ...FR,
    publishedTimesWithValidity: (date) =>
      `Horaires publiés disponibles jusqu'au ${date}`,
    fullJourney: "Voir le trajet",
    autoTheme: "Suivre l'appareil du visiteur",
  },
};

export function getFerryWidgetCopy(
  language: WidgetContentLanguage,
): FerryWidgetCopy {
  return COPIES[language] || EN;
}
